import type {
  DiretoriaChartKind,
  DiretoriaDataSource,
  DiretoriaIndicator,
  DiretoriaSeriesLabels,
} from '@/data/diretoriaTypes';

interface DiretoriaIndicatorCatalogEntry {
  id: string;
  title: string;
  shortDescription: string;
  primaryKpiIndex?: number;
  chartKind: DiretoriaChartKind;
  chartTitle?: string;
  barSeriesKey?: 'valor' | 'valor2';
  seriesLabels?: DiretoriaSeriesLabels;
  tableHeaders: string[];
  footnote: string;
  dataSource: DiretoriaDataSource;
  kpiLabels: [string, string, string];
}

export const DIRETORIA_INDICATOR_CATALOG: DiretoriaIndicatorCatalogEntry[] = [
  {
    id: 'custo-moto',
    title: 'Custo por moto',
    shortDescription: 'Custo atual por equipamento (compra, OS e financeiro).',
    primaryKpiIndex: 0,
    chartKind: 'barHorizontal',
    chartTitle: 'Custo médio por marca / modelo',
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Custo médio', valor2: 'Qtd.' },
    tableHeaders: ['Marca / modelo', 'Custo médio', 'Qtd.'],
    footnote: 'ERP FlyStart: tipo=moto, ativo=1.',
    dataSource: 'erp',
    kpiLabels: ['Média atual', 'Total atual', 'Motos (base)'],
  },
  {
    id: 'faturamento-bruto',
    title: 'Faturamento bruto geral',
    shortDescription: 'Receitas realizadas (baixas a receber) no ano e no mês.',
    primaryKpiIndex: 0,
    chartKind: 'line',
    chartTitle: 'Evolução mensal (ano de referência)',
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Faturamento' },
    tableHeaders: ['Unidade', 'Faturamento'],
    footnote: 'ERP FlyStart: contabaixa tipocontaid=1, cb.deleted_at IS NULL.',
    dataSource: 'erp',
    kpiLabels: ['Anual (YTD)', 'Mês atual', 'Var. vs mês ant.'],
  },
  {
    id: 'inadimplencia',
    title: 'Inadimplência',
    shortDescription: 'Títulos vencidos/parciais APROVADA — mesma base do aging de cobrança.',
    primaryKpiIndex: 0,
    chartKind: 'barGrouped',
    chartTitle: 'Títulos a receber vencidos por faixa',
    seriesLabels: { valor: 'Qtd.', valor2: 'Saldo' },
    tableHeaders: ['Faixa de atraso', 'Qtd.', 'Saldo'],
    footnote:
      'Cobrança: statusLocacao=APROVADA; VENCIDO/PARCIAL; vencimento < dataTo; exclui multas. Faixas 1–2 / 3–6 / 7+ dias.',
    dataSource: 'cobranca',
    kpiLabels: ['Saldo vencido', 'Títulos vencidos', 'Em 7+ dias'],
  },
  {
    id: 'infracoes',
    title: 'Infrações (multas)',
    shortDescription: 'Contas com multa_id, classificadas por estado.',
    primaryKpiIndex: 0,
    chartKind: 'barGrouped',
    chartTitle: 'Multas por estado',
    seriesLabels: { valor: 'Qtd.', valor2: 'Valor' },
    tableHeaders: ['Estado', 'Qtd.', 'Valor', 'Média'],
    footnote: 'ERP FlyStart: c.multa_id IS NOT NULL; Pagas = baixas no mês de dataTo; média = valor ÷ qtd.',
    dataSource: 'erp',
    kpiLabels: ['Pagas (mês ref.)', 'Vencidas', 'Média / multa'],
  },
  {
    id: 'motos-alugadas',
    title: 'Motos alugadas',
    shortDescription: 'Frota alugada e fluxo de locações.',
    primaryKpiIndex: 0,
    chartKind: 'barGrouped',
    chartTitle: 'Entradas e saídas',
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Novas', valor2: 'Canceladas' },
    tableHeaders: ['Mês', 'Entradas', 'Saídas'],
    footnote: 'ERP FlyStart: equipamentos + locacao.',
    dataSource: 'erp',
    kpiLabels: ['Alugadas (frota)', 'Novas no mês', 'Canceladas no mês'],
  },
  {
    id: 'rentabilidade-moto',
    title: 'Rentabilidade média por moto',
    shortDescription: 'Receita realizada vs custo atual por moto.',
    primaryKpiIndex: 2,
    chartKind: 'line',
    chartTitle: 'Receita vs custo (mensal)',
    seriesLabels: { valor: 'Receita', valor2: 'Custo' },
    tableHeaders: ['Componente', 'Valor'],
    footnote: 'ERP FlyStart: receita por equipamento + custo por moto.',
    dataSource: 'erp',
    kpiLabels: ['Média receita / moto', 'Média custo / moto', 'Margem'],
  },
  {
    id: 'motos-disponiveis',
    title: 'Motos disponíveis',
    shortDescription: 'Motos com status disponível por empresa e marca.',
    primaryKpiIndex: 0,
    chartKind: 'barHorizontal',
    chartTitle: 'Disponíveis por empresa · marca',
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Disponíveis' },
    tableHeaders: ['Empresa', 'Marca', 'Disponíveis'],
    footnote: 'ERP FlyStart: status_equipamento=disponivel. Gráfico por empresa · marca.',
    dataSource: 'erp',
    kpiLabels: ['Disponíveis hoje', 'Top (empresa · marca)', 'Combinações'],
  },
  {
    id: 'motos-status',
    title: 'Motos por status',
    shortDescription: 'Distribuição da frota por status operacional.',
    primaryKpiIndex: 0,
    chartKind: 'pie',
    chartTitle: 'Composição da frota por status',
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Qtd.', valor2: '% frota' },
    tableHeaders: ['Status', 'Qtd.', '% frota'],
    footnote: 'ERP FlyStart: tipo=moto, ativo=1. Distribuição por status (rótulos amigáveis).',
    dataSource: 'erp',
    kpiLabels: ['Total na base', 'Maior status', 'Situações críticas'],
  },
  {
    id: 'locacao-local',
    title: 'Locação por bairro / cidade',
    shortDescription: 'Locações ativas por endereço do locatário.',
    primaryKpiIndex: 1,
    chartKind: 'barHorizontal',
    chartTitle: 'Top cidades',
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Locações' },
    tableHeaders: ['Bairro', 'Cidade', 'Locações'],
    footnote: 'ERP FlyStart: locacao + pessoa_crm.',
    dataSource: 'erp',
    kpiLabels: ['Cidades', 'Top cidade', 'Contratos (mês)'],
  },
];

const SOURCE_HINT: Record<DiretoriaDataSource, string> = {
  erp: 'ERP FlyStart',
  cobranca: 'Cobrança',
  oficina: 'Oficina',
};

export function createPlaceholderIndicator(entry: DiretoriaIndicatorCatalogEntry): DiretoriaIndicator {
  return {
    id: entry.id,
    title: entry.title,
    shortDescription: entry.shortDescription,
    primaryKpiIndex: entry.primaryKpiIndex ?? 0,
    kpis: entry.kpiLabels.map((label) => ({
      label,
      value: '—',
      hint: SOURCE_HINT[entry.dataSource],
      tone: 'neutral',
    })),
    chartKind: entry.chartKind,
    chartTitle: entry.chartTitle,
    barSeriesKey: entry.barSeriesKey,
    seriesLabels: entry.seriesLabels,
    series: [],
    pieData: [],
    tableHeaders: entry.tableHeaders,
    tableRows: [],
    footnote: entry.footnote,
    dataSource: entry.dataSource,
    hasData: false,
  };
}

export function getDiretoriaIndicatorCatalog(): DiretoriaIndicatorCatalogEntry[] {
  return DIRETORIA_INDICATOR_CATALOG;
}

export function getPlaceholderIndicators(): DiretoriaIndicator[] {
  return DIRETORIA_INDICATOR_CATALOG.map(createPlaceholderIndicator);
}
